import { z } from '../zod';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';

export const CancelSubscriptionParamsValidationSchema = z.object({
  subscriptionId: ObjectIdStringValidationSchema.meta({
    description: 'The ID of the subscription to cancel.',
  }),
});

const CANCEL_TIMING_FIELDS = ['cancelImmediately', 'cancelAtPeriodEnd', 'cancelAt'] as const;

export const CancelSubscriptionRequestBodyValidationSchema = z
  .object({
    reason: z.string().trim().min(1).max(500).optional().meta({
      description:
        'Free-form note describing why the subscription is being canceled. Stored on the subscription record.',
    }),
    cancelImmediately: z.literal(true).optional().meta({
      description:
        'When `true`, the subscription is canceled immediately. Mutually exclusive with `cancelAtPeriodEnd` and `cancelAt`.',
    }),
    cancelAtPeriodEnd: z.literal(true).optional().meta({
      description:
        'When `true`, the subscription is set to cancel at the end of the current billing period. Mutually exclusive with `cancelImmediately` and `cancelAt`.',
    }),
    cancelAt: z.iso
      .datetime()
      .refine(value => new Date(value).getTime() > Date.now(), {
        message: 'cancelAt must be a future timestamp',
      })
      .optional()
      .meta({
        description:
          'ISO 8601 timestamp for a scheduled future cancellation. Mutually exclusive with `cancelImmediately` and `cancelAtPeriodEnd`.',
      }),
    sendCancellationEmail: z.boolean().optional().meta({
      description: 'When `true`, sends a cancellation notification email to the customer.',
    }),
  })
  .strict()
  .superRefine((data, ctx) => {
    const active = CANCEL_TIMING_FIELDS.filter(field => data[field] !== undefined);
    if (active.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Exactly one of cancelImmediately, cancelAtPeriodEnd, or cancelAt must be set',
        path: [],
      });
    } else if (active.length > 1) {
      ctx.addIssue({
        code: 'custom',
        message: `Only one of cancelImmediately, cancelAtPeriodEnd, or cancelAt may be set (got: ${active.join(', ')})`,
        path: [],
      });
    }
  });

const CancelSubscriptionSuccessDataValidationSchema = z.object({
  success: z.boolean().meta({
    description: 'Always `true` when the cancellation request succeeded.',
  }),
});

export const CancelSubscriptionSuccessResponseValidationSchema = z.object({
  data: CancelSubscriptionSuccessDataValidationSchema,
});
